import { useCallback, useEffect, useMemo, useState } from "react";
import { api, mesajEroare } from "./api.js";
import {
  CEASURI_SLA, CRITICITATI, SEVERITATI, STARI_SISTEM, STARI_VIZITA,
  TIPURI_GARANTIE, eticheta,
} from "./enums.js";
import { data } from "./text.js";
import { exportaExcel, exportaPdf } from "./export.js";
import { tr } from "./i18n.js";

const lei = (v) =>
  v === null || v === undefined ? "—"
    : Number(v).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " lei";
const proc = (v) => (v === null || v === undefined ? "—" : `${v}%`);
const nr = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("ro-RO"));
/** „746706 ore" nu se verifică din ochi; „746.706 (≈85 de ani)" da. */
const ore = (v) => {
  if (v === null || v === undefined) return "—";
  const ani = v / 8760;
  return ani >= 2 ? `${nr(v)} (≈${Math.round(ani)} ani)` : nr(v);
};

/** „0h 27m" nu se citește ca 27 de minute — ora se scrie doar când există. */
const durata = (m) => {
  if (m === null || m === undefined) return "—";
  if (m < 60) return `${Math.round(m)}m`;
  const o = Math.floor(m / 60); const min = Math.round(m % 60);
  return min ? `${o}h ${min}m` : `${o}h`;
};

const LUNI = ["ian", "feb", "mar", "apr", "mai", "iun", "iul", "aug", "sep", "oct", "noi", "dec"];
const luna = (v) => {
  const [an, l] = String(v).split("-");
  return LUNI[Number(l) - 1] ? `${tr(LUNI[Number(l) - 1])} ${an}` : v;
};

/**
 * MT16 — rapoarte, KPI și tablou de bord.
 *
 * Graficele sunt SVG scris de mână, nu Recharts. Abatere deliberată de la
 * litera SoW-ului, spusă pe față: patru grafice simple (o linie, două bare)
 * nu justifică o bibliotecă de câteva sute de kilobytes într-o aplicație al
 * cărei pachet principal e deja mare, și pe care ar plăti-o la fiecare
 * încărcare inclusiv cine nu deschide niciodată tabloul de bord. Dacă apar
 * grafice care chiar cer o bibliotecă — zoom, brush, combinații — se adaugă
 * atunci, ca decizie separată.
 */
export default function Rapoarte() {
  const [sub, setSub] = useState("tablou");
  const [filtru, setFiltru] = useState({ de: "", pana: "", client: "", locatie: "", tehnician: "" });
  const [aplicat, setAplicat] = useState({});
  const [clienti, setClienti] = useState([]);
  const [tehnicieni, setTehnicieni] = useState([]);

  useEffect(() => {
    api.clienti().then((l) => setClienti(Array.isArray(l) ? l : l?.linii ?? [])).catch(() => {});
    api.tehnicieni?.().then((l) => setTehnicieni(Array.isArray(l) ? l : l?.linii ?? [])).catch(() => {});
  }, []);

  const curat = (f) => Object.fromEntries(Object.entries(f).filter(([, v]) => v));

  const SUBTABURI = [
    ["tablou", "Tablou de bord"],
    ["sla", "Conformitate SLA"],
    ["timpi", "MTTA / MTTR"],
    ["fiabilitate", "Fiabilitatea parcului"],
    ["rentabilitate", "Rentabilitate"],
    ["productivitate", "Productivitate"],
    ["scadentar", "Scadențar"],
    ["consum", "Consum materiale"],
    ["garantii", "Garanții"],
    ["registru", "Registru intervenții"],
  ];

  return (
    <div className="continut">
      <h2>{tr("Rapoarte și tablou de bord")}</h2>
      <p className="subtitlu">
        {tr("Toate rapoartele se calculează la citire din fișele și ceasurile reale — niciunul nu e un total salvat care poate rămâne în urmă.")}
      </p>

      <div className="linie-actiune">
        <label className="camp">{tr("De la")}
          <input type="date" value={filtru.de}
            onChange={(e) => setFiltru({ ...filtru, de: e.target.value })} />
        </label>
        <label className="camp">{tr("Până la")}
          <input type="date" value={filtru.pana}
            onChange={(e) => setFiltru({ ...filtru, pana: e.target.value })} />
        </label>
        <label className="camp">{tr("Client")}
          <select value={filtru.client}
            onChange={(e) => setFiltru({ ...filtru, client: e.target.value, locatie: "" })}>
            <option value="">{tr("toți clienții")}</option>
            {clienti.map((c) => <option key={c.id} value={c.id}>{tr(c.denumire)}</option>)}
          </select>
        </label>
        {tehnicieni.length > 0 && (
          <label className="camp">{tr("Tehnician")}
            <select value={filtru.tehnician}
              onChange={(e) => setFiltru({ ...filtru, tehnician: e.target.value })}>
              <option value="">{tr("toți tehnicienii")}</option>
              {tehnicieni.map((t) => <option key={t.id} value={t.id}>{t.numeComplet}</option>)}
            </select>
          </label>
        )}
        <button className="buton" onClick={() => setAplicat(curat(filtru))}>{tr("aplică")}</button>
        <button className="buton-mic" onClick={() => {
          setFiltru({ de: "", pana: "", client: "", locatie: "", tehnician: "" });
          setAplicat({});
        }}>{tr("șterge filtrele")}</button>
      </div>

      <div className="taburi-sisteme">
        {SUBTABURI.map(([cod, titlu]) => (
          <button key={cod} className={`tab-sistem ${sub === cod ? "activ" : ""}`}
            onClick={() => setSub(cod)}>{tr(titlu)}</button>
        ))}
      </div>

      {sub === "tablou" && <Tablou filtru={aplicat} />}
      {sub === "sla" && <Sla filtru={aplicat} />}
      {sub === "timpi" && <Timpi filtru={aplicat} />}
      {sub === "fiabilitate" && <Fiabilitate filtru={aplicat} />}
      {sub === "rentabilitate" && <Rentabilitate filtru={aplicat} />}
      {sub === "productivitate" && <Productivitate filtru={aplicat} />}
      {sub === "scadentar" && <Scadentar filtru={aplicat} />}
      {sub === "consum" && <Consum filtru={aplicat} />}
      {sub === "garantii" && <Garantii filtru={aplicat} />}
      {sub === "registru" && <Registru filtru={aplicat} />}
    </div>
  );
}

// ── Schelărie comună ──────────────────────────────────────────────────────

function useRaport(fn, filtru) {
  const [date, setDate] = useState(null);
  const [eroare, setEroare] = useState("");
  // Un refuz de drept NU e o eroare. Rentabilitatea și consumul de materiale
  // sunt rapoarte de valori, iar dispecerul, coordonatorul și tehnicianul nu
  // au dreptul la bani — deci răspunsul corect e 403, de fiecare dată, pentru
  // totdeauna. Afișat ca bandă roșie, arată ca o aplicație stricată exact
  // acolo unde funcționează perfect. Serverul spune deja de ce, într-o
  // propoziție; ea se arată ca explicație, nu ca defect.
  const [interzis, setInterzis] = useState("");
  const [incarca, setIncarca] = useState(true);
  const cheie = JSON.stringify(filtru);
  const rulează = useCallback(() => {
    setIncarca(true); setEroare(""); setInterzis("");
    fn(filtru).then(setDate).catch((e) => {
      setDate(null);
      if (e?.status === 403) setInterzis(mesajEroare(e));
      else setEroare(mesajEroare(e));
    }).finally(() => setIncarca(false));
    // Filtrul se compară pe conținut, nu pe identitate: un obiect nou cu
    // aceleași valori ar reîncărca raportul la fiecare randare.
  }, [cheie]);
  useEffect(() => { rulează(); }, [rulează]);
  return { date, eroare, interzis, incarca };
}

/** Bara de export, identică pe toate rapoartele. */
function Export({ raport, titlu, filtru, tabele }) {
  const [lucreaza, setLucreaza] = useState("");
  const [eroare, setEroare] = useState("");
  const fa = async (fel) => {
    setLucreaza(fel); setEroare("");
    try {
      if (fel === "xlsx") await exportaExcel(raport, filtru, tabele());
      else await exportaPdf(raport, titlu, filtru, tabele());
    } catch (e) { setEroare(e.message || tr("Exportul a eșuat.")); }
    finally { setLucreaza(""); }
  };
  return (
    <div className="rand-export">
      <button className="buton-mic" disabled={!!lucreaza} onClick={() => fa("xlsx")}>
        {lucreaza === "xlsx" ? tr("se generează…") : "Excel"}
      </button>
      <button className="buton-mic" disabled={!!lucreaza} onClick={() => fa("pdf")}>
        {lucreaza === "pdf" ? tr("se generează…") : "PDF"}
      </button>
      {eroare && <span className="eroare-mica">{eroare}</span>}
    </div>
  );
}

function Panou({ titlu, subtitlu, children, export: exp }) {
  return (
    <div className="panou">
      <div className="panou-antet">
        <div>
          <h3>{tr(titlu)}</h3>
          {subtitlu && <p className="subtitlu">{tr(subtitlu)}</p>}
        </div>
        {exp}
      </div>
      <div className="panou-corp">{children}</div>
    </div>
  );
}

function Tabel({ coloane, randuri, gol = "Niciun rând." }) {
  if (!randuri?.length) return <p className="gol">{gol}</p>;
  return (
    <div className="tabel-derulabil">
      <table className="tabel">
        <thead><tr>{coloane.map((c) => <th key={c.cheie}>{tr(c.eticheta)}</th>)}</tr></thead>
        <tbody>
          {randuri.map((r, i) => (
            <tr key={i}>
              {coloane.map((c) => (
                <td key={c.cheie} className={c.clasa?.(r) ?? ""}>
                  {c.randare ? c.randare(r) : (c.valoare ? c.valoare(r) : r[c.cheie]) ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const Stare = ({ incarca, eroare, interzis, gol }) => {
  if (incarca) return <p className="gol">{tr("Se încarcă…")}</p>;
  if (eroare) return <p className="eroare">{eroare}</p>;
  // Explicație, nu defect: funcția nu are dreptul, iar asta nu se schimbă
  // dacă mai apeși o dată.
  if (interzis) return <p className="gol">{interzis}</p>;
  if (gol) return <p className="gol">{tr("Nimic de raportat în perioada aleasă.")}</p>;
  return null;
};

// ── Grafice ───────────────────────────────────────────────────────────────

/**
 * Bare verticale, cu axa pornind de la ZERO.
 *
 * O axă tăiată face dintr-o diferență de 2% un munte — pe un raport de
 * conformitate, exact minciuna care se citește cel mai ușor.
 */
function Bare({ serie, format = (v) => v, inaltime = 140 }) {
  const max = Math.max(1, ...serie.map((s) => s.valoare ?? 0));
  if (!serie.length) return <p className="gol">{tr("Nicio lună cu date.")}</p>;
  // Un grafic în care toate valorile sunt zero nu e un grafic, e o linie
  // dreaptă pe care cititorul o ia drept defect de randare.
  if (serie.every((s) => !s.valoare)) {
    return <p className="gol">{tr("Toate valorile din perioadă sunt zero — nimic de desenat.")}</p>;
  }
  return (
    <div className="grafic">
      <div className="grafic-bare" style={{ height: inaltime }}>
        {serie.map((s, i) => (
          <div key={i} className="grafic-coloana" title={`${s.eticheta}: ${format(s.valoare)}`}>
            <span className="grafic-valoare">{format(s.valoare)}</span>
            <div className="grafic-bara"
              style={{ height: `${Math.round(((s.valoare ?? 0) / max) * 100)}%` }} />
            <span className="grafic-eticheta" title={tr(s.eticheta)}>{tr(s.eticheta)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Două serii suprapuse pe aceeași lună — în termen față de depășite. */
function BareDuble({ serie, inaltime = 160 }) {
  const max = Math.max(1, ...serie.map((s) => (s.a ?? 0) + (s.b ?? 0)));
  if (!serie.length) return <p className="gol">{tr("Nicio lună cu date.")}</p>;
  return (
    <div className="grafic">
      <div className="grafic-bare" style={{ height: inaltime }}>
        {serie.map((s, i) => (
          <div key={i} className="grafic-coloana"
            title={tr("{0}: {1} în termen, {2} depășite", s.eticheta, s.a, s.b)}>
            <span className="grafic-valoare">{(s.a ?? 0) + (s.b ?? 0)}</span>
            <div className="grafic-stiva">
              <div className="grafic-bara grafic-rau"
                style={{ height: `${Math.round(((s.b ?? 0) / max) * 100)}%` }} />
              <div className="grafic-bara"
                style={{ height: `${Math.round(((s.a ?? 0) / max) * 100)}%` }} />
            </div>
            <span className="grafic-eticheta" title={tr(s.eticheta)}>{tr(s.eticheta)}</span>
          </div>
        ))}
      </div>
      <div className="grafic-legenda">
        <span><i className="pastila-bine" /> {tr("în termen")}</span>
        <span><i className="pastila-rau" /> {tr("depășite")}</span>
      </div>
    </div>
  );
}

function Cifra({ eticheta: et, valoare, sub, stare }) {
  return (
    <div className={`cifra ${stare ?? ""}`}>
      <span className="cifra-eticheta">{tr(et)}</span>
      <strong className="cifra-valoare">{valoare}</strong>
      {sub && <span className="cifra-sub">{tr(sub)}</span>}
    </div>
  );
}

// ── 1. Tabloul de bord ────────────────────────────────────────────────────

function Tablou({ filtru }) {
  const { date, eroare, interzis, incarca } = useRaport(api.rapTablou, filtru);
  if (incarca || eroare || interzis) return <Stare incarca={incarca} eroare={eroare} interzis={interzis} />;
  if (!date) return null;
  const { sla, timpi, interventii, scadente, garantii, bani } = date;

  return (
    <>
      <div className="panou">
        <div className="panou-corp cifre">
          <Cifra eticheta={tr("Tichete în termen")} valoare={proc(sla.procentInTermen)}
            sub={`${sla.inTermen} din ${sla.total}`}
            stare={sla.procentInTermen !== null && sla.procentInTermen < 90 ? "cifra-rea" : ""} />
          <Cifra eticheta={tr("Timp mediu de reacție")} valoare={durata(timpi.mtta?.mediaMinute)}
            sub={timpi.mtta ? `mediana ${durata(timpi.mtta.medianaMinute)}` : tr("fără date")} />
          <Cifra eticheta={tr("Timp mediu de restaurare")} valoare={durata(timpi.mttr?.mediaMinute)}
            sub={timpi.mttr ? `mediana ${durata(timpi.mttr.medianaMinute)}` : tr("fără date")} />
          <Cifra eticheta={tr("Rezolvate din prima")} valoare={proc(interventii.procentDinPrima)}
            sub={tr("din {0} intervenții", interventii.total)} />
          <Cifra eticheta={tr("Mentenanțe întârziate")} valoare={scadente.intarziate}
            sub={tr("{0} în 30 de zile", scadente.in30)}
            stare={scadente.intarziate > 0 ? "cifra-rea" : ""} />
          <Cifra eticheta={tr("Sisteme fără plan")} valoare={scadente.faraPlan}
            sub={tr("scadente, dar neplanificate")}
            stare={scadente.faraPlan > 0 ? "cifra-atentie" : ""} />
          <Cifra eticheta={tr("Garanții expirate")} valoare={garantii.expirate}
            sub={tr("{0} încă active", garantii.active)} />
          {bani && <Cifra eticheta={tr("Marjă")} valoare={proc(bani.marjaProcent)}
            sub={`${lei(bani.marja)} din ${lei(bani.venit)}`}
            stare={bani.marjaProcent !== null && bani.marjaProcent < 15 ? "cifra-rea" : ""} />}
        </div>
      </div>

      <Panou titlu={tr("Conformitatea SLA, pe luni")}
        subtitlu={tr("Coloana întreagă e totalul tichetelor; partea roșie, cele ieșite din termen.")}>
        <BareDuble serie={sla.peLuna.map((l) => ({
          eticheta: luna(l.luna), a: l.inTermen, b: l.depasite,
        }))} />
      </Panou>

      <Panou titlu={tr("Timpul de restaurare, pe luni")}
        subtitlu={tr("Mediana, nu media: un tichet ținut deschis peste sărbători mută media atât cât s-o facă de necitit.")}>
        <Bare serie={timpi.peLuna.filter((l) => l.mttr).map((l) => ({
          eticheta: luna(l.luna), valoare: l.mttr.medianaMinute,
        }))} format={durata} />
      </Panou>

      {bani && (
        <Panou titlu={tr("Marja, pe luni")}
          subtitlu={tr("Venitul liniilor facturabile minus costul real al pieselor și al manoperei.")}>
          <Bare serie={bani.peLuna.map((l) => ({ eticheta: luna(l.nume), valoare: l.marja }))}
            format={(v) => lei(v)} />
        </Panou>
      )}
    </>
  );
}

// ── 2. Conformitate SLA ───────────────────────────────────────────────────

function Sla({ filtru }) {
  const { date, eroare, interzis, incarca } = useRaport(api.rapSla, filtru);
  const colSev = [
    { cheie: "severitate", eticheta: "Severitate", valoare: (r) => eticheta(SEVERITATI, r.severitate) },
    { cheie: "total", eticheta: "Total" },
    { cheie: "inTermen", eticheta: "În termen" },
    { cheie: "depasite", eticheta: "Depășite" },
    { cheie: "procentInTermen", eticheta: "Procent", valoare: (r) => proc(r.procentInTermen) },
  ];
  const colLuna = [{ cheie: "luna", eticheta: "Luna", valoare: (r) => luna(r.luna) }, ...colSev.slice(1)];
  const colDep = [
    { cheie: "tichet", eticheta: "Tichet" },
    { cheie: "titlu", eticheta: "Titlu" },
    { cheie: "client", eticheta: "Client" },
    { cheie: "severitate", eticheta: "Severitate", valoare: (r) => eticheta(SEVERITATI, r.severitate) },
    { cheie: "tipCeas", eticheta: "Ceas", valoare: (r) => eticheta(CEASURI_SLA, r.tipCeas) },
    { cheie: "termenLa", eticheta: "Termen", valoare: (r) => data(r.termenLa) },
    { cheie: "minuteIntarziere", eticheta: "Întârziere", valoare: (r) => durata(r.minuteIntarziere) },
    { cheie: "inca", eticheta: "Stare", valoare: (r) => (r.inca ? "încă deschis" : "închis") },
  ];
  if (incarca || eroare || interzis || !date) return <Stare incarca={incarca} eroare={eroare} interzis={interzis} />;

  return (
    <>
      <Panou titlu={tr("Conformitate SLA")}
        subtitlu={tr("{0} din {1} ceasuri în termen ({2}).", date.inTermen, date.total, proc(date.procentInTermen))}
        export={<Export raport="conformitate-sla" titlu={tr("Conformitate SLA")} filtru={filtru}
          tabele={() => [
            { titlu: "Pe severitate", coloane: colSev, randuri: date.peSeveritate },
            { titlu: "Pe luni", coloane: colLuna, randuri: date.peLuna },
            { titlu: "Depășiri", coloane: colDep, randuri: date.depasiri },
          ]} />}>
        <BareDuble serie={date.peLuna.map((l) => ({
          eticheta: luna(l.luna), a: l.inTermen, b: l.depasite,
        }))} />
        <h4>{tr("Pe severitate")}</h4>
        <Tabel coloane={colSev} randuri={date.peSeveritate} />
      </Panou>
      <Panou titlu={tr("Unde s-a depășit")}
        subtitlu={tr("Ordonate după cât de mult s-a depășit, nu după dată.")}>
        <Tabel coloane={colDep} randuri={date.depasiri} gol="Nicio depășire în perioada aleasă." />
      </Panou>
    </>
  );
}

// ── 3. MTTA / MTTR ────────────────────────────────────────────────────────

function Timpi({ filtru }) {
  const { date, eroare, interzis, incarca } = useRaport(api.rapTimpi, filtru);
  const col = [
    { cheie: "luna", eticheta: "Luna", valoare: (r) => luna(r.luna) },
    { cheie: "mttaNumar", eticheta: "Reacții", valoare: (r) => r.mtta?.numar ?? 0 },
    { cheie: "mttaMedia", eticheta: "MTTA mediu", valoare: (r) => durata(r.mtta?.mediaMinute) },
    { cheie: "mttaMediana", eticheta: "MTTA median", valoare: (r) => durata(r.mtta?.medianaMinute) },
    { cheie: "mttrNumar", eticheta: "Restaurări", valoare: (r) => r.mttr?.numar ?? 0 },
    { cheie: "mttrMedia", eticheta: "MTTR mediu", valoare: (r) => durata(r.mttr?.mediaMinute) },
    { cheie: "mttrMediana", eticheta: "MTTR median", valoare: (r) => durata(r.mttr?.medianaMinute) },
  ];
  if (incarca || eroare || interzis || !date) return <Stare incarca={incarca} eroare={eroare} interzis={interzis} />;

  return (
    <Panou titlu={tr("Timp de reacție și de restaurare")}
      subtitlu={tr("Măsurat pe ceasuri, cu timpul de pauză scăzut: un ceas oprit fiindcă așteptăm o piesă de la client nu e întârzierea noastră. Se numără doar ceasurile închise.")}
      export={<Export raport="mtta-mttr" titlu={tr("MTTA / MTTR")} filtru={filtru}
        tabele={() => [{ titlu: "Pe luni", coloane: col, randuri: date.peLuna }]} />}>
      <div className="cifre">
        <Cifra eticheta={tr("MTTA mediu")} valoare={durata(date.mtta?.mediaMinute)}
          sub={date.mtta ? tr("mediană {0} · {1} ceasuri", durata(date.mtta.medianaMinute), date.mtta.numar) : tr("fără date")} />
        <Cifra eticheta={tr("MTTR mediu")} valoare={durata(date.mttr?.mediaMinute)}
          sub={date.mttr ? tr("mediană {0} · {1} ceasuri", durata(date.mttr.medianaMinute), date.mttr.numar) : tr("fără date")} />
        <Cifra eticheta={tr("Cea mai lungă restaurare")} valoare={durata(date.mttr?.celMaiLungMinute)} />
      </div>
      <Bare serie={date.peLuna.filter((l) => l.mttr).map((l) => ({
        eticheta: luna(l.luna), valoare: l.mttr.medianaMinute,
      }))} format={durata} />
      <Tabel coloane={col} randuri={date.peLuna} />
    </Panou>
  );
}

// ── 4. Fiabilitatea parcului ──────────────────────────────────────────────

function Fiabilitate({ filtru }) {
  const { date, eroare, interzis, incarca } = useRaport(api.rapFiabilitate, filtru);
  const colTip = [
    { cheie: "cod", eticheta: "Cod" },
    { cheie: "denumire", eticheta: "Tip de componentă" },
    { cheie: "echipamente", eticheta: "Bucăți" },
    { cheie: "defecte", eticheta: "Defecte" },
    { cheie: "oreAcumulate", eticheta: "Ore acumulate", valoare: (r) => ore(r.oreAcumulate) },
    { cheie: "mtbfOre", eticheta: "MTBF (ore)", valoare: (r) => ore(r.mtbfOre) },
  ];
  const colTop = [
    { cheie: "echipament", eticheta: "Echipament" },
    { cheie: "serie", eticheta: "Serie" },
    { cheie: "tip", eticheta: "Tip" },
    { cheie: "client", eticheta: "Client" },
    { cheie: "locatie", eticheta: "Locație" },
    { cheie: "defecte", eticheta: "Defecte" },
    { cheie: "inlocuiri", eticheta: "Înlocuiri" },
    { cheie: "ultimul", eticheta: "Ultimul defect", valoare: (r) => data(r.ultimul) },
    { cheie: "stare", eticheta: "Stare", valoare: (r) => eticheta(STARI_SISTEM, r.stare) },
  ];
  if (incarca || eroare || interzis || !date) return <Stare incarca={incarca} eroare={eroare} interzis={interzis} />;

  return (
    <>
      <Panou titlu={tr("MTBF pe tip de componentă")} subtitlu={date.bazaDeCalcul}
        export={<Export raport="fiabilitate-parc" titlu={tr("Fiabilitatea parcului")} filtru={filtru}
          tabele={() => [
            { titlu: "MTBF pe tip", coloane: colTip, randuri: date.peTipDeComponenta },
            { titlu: "Fara defecte", coloane: colTip, randuri: date.faraDefecte },
            { titlu: "Top componente", coloane: colTop, randuri: date.topComponente },
          ]} />}>
        <Tabel coloane={colTip} randuri={date.peTipDeComponenta}
          gol="Niciun defect consemnat pe tipurile din filtru." />
      </Panou>
      <Panou titlu={tr("Tipuri fără niciun defect")}
        subtitlu={tr("Nu primesc MTBF — n-au avut încă un „între defecte”. Orele acumulate spun singure cât au ținut.")}>
        <Tabel coloane={colTip.filter((c) => c.cheie !== "mtbfOre")} randuri={date.faraDefecte} />
      </Panou>
      <Panou titlu={tr("Primele zece componente cu defecte")}>
        <Tabel coloane={colTop} randuri={date.topComponente} />
      </Panou>
    </>
  );
}

// ── 5. Rentabilitate ──────────────────────────────────────────────────────

function Rentabilitate({ filtru }) {
  const { date, eroare, interzis, incarca } = useRaport(api.rapRentabilitate, filtru);
  const col = [
    { cheie: "nume", eticheta: "Client" },
    { cheie: "interventii", eticheta: "Intervenții" },
    { cheie: "venit", eticheta: "Venit", valoare: (r) => lei(r.venit) },
    { cheie: "cost", eticheta: "Cost intern", valoare: (r) => lei(r.cost) },
    { cheie: "neFacturat", eticheta: "Nefacturat", valoare: (r) => lei(r.neFacturat) },
    { cheie: "marja", eticheta: "Marjă", valoare: (r) => lei(r.marja) },
    {
      cheie: "marjaProcent", eticheta: "Marjă %", valoare: (r) => proc(r.marjaProcent),
      clasa: (r) => (r.marjaProcent !== null && r.marjaProcent < 0 ? "vechime-90" : ""),
    },
  ];
  const colLuna = [{ cheie: "nume", eticheta: "Luna", valoare: (r) => luna(r.nume) }, ...col.slice(1)];
  if (incarca || eroare || interzis || !date) return <Stare incarca={incarca} eroare={eroare} interzis={interzis} />;

  return (
    <>
      <Panou titlu={tr("Rentabilitatea contractelor")}
        subtitlu={tr("Venitul e suma liniilor facturabile la prețurile înghețate la validare. Costul e costul real al pieselor plus manopera la costul ei. Ce e acoperit din garanție sau inclus în abonament nu aduce venit, dar costă — acolo stă informația.")}
        export={<Export raport="rentabilitate" titlu={tr("Rentabilitatea contractelor")} filtru={filtru}
          tabele={() => [
            { titlu: "Pe client", coloane: col, randuri: date.peClient },
            { titlu: "Pe luni", coloane: colLuna, randuri: date.peLuna },
          ]} />}>
        <div className="cifre">
          <Cifra eticheta={tr("Venit")} valoare={lei(date.venit)} />
          <Cifra eticheta={tr("Cost intern")} valoare={lei(date.cost)} />
          <Cifra eticheta={tr("Marjă")} valoare={lei(date.marja)} sub={proc(date.marjaProcent)}
            stare={date.marjaProcent !== null && date.marjaProcent < 15 ? "cifra-rea" : ""} />
        </div>
        <Tabel coloane={col} randuri={date.peClient} />
      </Panou>
      <Panou titlu={tr("Pe luni")}>
        <Bare serie={date.peLuna.map((l) => ({ eticheta: luna(l.nume), valoare: l.marja }))}
          format={(v) => lei(v)} />
        <Tabel coloane={colLuna} randuri={date.peLuna} />
      </Panou>
    </>
  );
}

// ── 6. Productivitate ─────────────────────────────────────────────────────

function Productivitate({ filtru }) {
  const { date, eroare, interzis, incarca } = useRaport(api.rapProductivitate, filtru);
  const col = [
    { cheie: "tehnician", eticheta: "Tehnician" },
    { cheie: "interventii", eticheta: "Intervenții" },
    { cheie: "zileLucrate", eticheta: "Zile lucrate" },
    { cheie: "interventiiPeZi", eticheta: "Pe zi" },
    { cheie: "minuteLucru", eticheta: "Timp de lucru", valoare: (r) => durata(r.minuteLucru) },
    { cheie: "minuteDeplasare", eticheta: "Deplasare", valoare: (r) => durata(r.minuteDeplasare) },
    { cheie: "procentTimpProductiv", eticheta: "Timp productiv", valoare: (r) => proc(r.procentTimpProductiv) },
    { cheie: "km", eticheta: "Km", valoare: (r) => nr(r.km) },
    {
      cheie: "procentDinPrima", eticheta: "Din prima", valoare: (r) => proc(r.procentDinPrima),
      clasa: (r) => (r.procentDinPrima !== null && r.procentDinPrima < 80 ? "vechime-60" : ""),
    },
  ];
  if (incarca || eroare || interzis || !date) return <Stare incarca={incarca} eroare={eroare} interzis={interzis} />;

  return (
    <Panou titlu={tr("Productivitatea tehnicienilor")}
      subtitlu={tr("„Zi lucrată” înseamnă o zi în care omul chiar a avut o fișă, nu zilele calendaristice ale perioadei. Rata de rezolvare din prima se numără pe fișele care nu cer revenire.")}
      export={<Export raport="productivitate" titlu={tr("Productivitatea tehnicienilor")} filtru={filtru}
        tabele={() => [{ titlu: "Pe tehnician", coloane: col, randuri: date.peTehnician }]} />}>
      <div className="cifre">
        <Cifra eticheta={tr("Intervenții")} valoare={date.total} />
        <Cifra eticheta={tr("Rezolvate din prima")} valoare={proc(date.procentDinPrima)} />
      </div>
      <Bare serie={date.peTehnician.slice(0, 12).map((t) => ({
        eticheta: t.tehnician.split(" ")[0], valoare: t.interventii,
      }))} />
      <Tabel coloane={col} randuri={date.peTehnician} />
    </Panou>
  );
}

// ── 7. Scadențar ──────────────────────────────────────────────────────────

function Scadentar({ filtru }) {
  const { date, eroare, interzis, incarca } = useRaport(api.rapScadentar, filtru);
  const col = [
    { cheie: "client", eticheta: "Client" },
    { cheie: "locatie", eticheta: "Locație" },
    { cheie: "sistem", eticheta: "Sistem" },
    { cheie: "tip", eticheta: "Tip" },
    { cheie: "criticitate", eticheta: "Criticitate", valoare: (r) => eticheta(CRITICITATI, r.criticitate) },
    { cheie: "ultimaMentenanta", eticheta: "Ultima", valoare: (r) => data(r.ultimaMentenanta) },
    { cheie: "scadentLa", eticheta: "Scadent la", valoare: (r) => data(r.scadentLa) },
    {
      cheie: "zile", eticheta: "Zile", valoare: (r) => (r.zile < 0 ? `întârziat cu ${-r.zile}` : r.zile),
      clasa: (r) => (r.zile < 0 ? "vechime-90" : r.zile <= 30 ? "vechime-60" : ""),
    },
    { cheie: "faraPlan", eticheta: "Plan", valoare: (r) => (r.faraPlan ? "fără plan" : "planificat") },
  ];
  if (incarca || eroare || interzis || !date) return <Stare incarca={incarca} eroare={eroare} interzis={interzis} />;
  const grupe = [
    ["Întârziate", date.intarziate], ["În 30 de zile", date.in30],
    ["În 60 de zile", date.in60], ["În 90 de zile", date.in90],
  ];

  return (
    <>
      <Panou titlu={tr("Scadențarul de mentenanță")}
        subtitlu={tr("Citit din parcul instalat, nu din planuri: un sistem poate avea o scadență fără să aibă încă un plan, iar acela e chiar cazul care trebuie să sară în ochi.")}
        export={<Export raport="scadentar" titlu={tr("Scadențar mentenanță")} filtru={filtru}
          tabele={() => grupe.map(([t, r]) => ({ titlu: t, coloane: col, randuri: r }))} />}>
        <div className="cifre">
          {grupe.map(([t, r]) => (
            <Cifra key={t} eticheta={t} valoare={r.length}
              stare={t === "Întârziate" && r.length > 0 ? "cifra-rea" : ""} />
          ))}
          <Cifra eticheta={tr("Fără plan")} valoare={date.faraPlan}
            stare={date.faraPlan > 0 ? "cifra-atentie" : ""} />
        </div>
      </Panou>
      {grupe.map(([t, r]) => (
        <Panou key={t} titlu={t}>
          <Tabel coloane={col} randuri={r} gol="Nimic în această fereastră." />
        </Panou>
      ))}
    </>
  );
}

// ── 8. Consum materiale ───────────────────────────────────────────────────

function Consum({ filtru }) {
  const { date, eroare, interzis, incarca } = useRaport(api.rapConsum, filtru);
  const colArt = [
    { cheie: "cod", eticheta: "Cod" },
    { cheie: "denumire", eticheta: "Articol" },
    { cheie: "cantitate", eticheta: "Cantitate", valoare: (r) => nr(r.cantitate) },
    { cheie: "um", eticheta: "UM" },
    { cheie: "linii", eticheta: "Linii" },
    { cheie: "valoare", eticheta: "Valoare", valoare: (r) => lei(r.valoare) },
    { cheie: "cost", eticheta: "Cost", valoare: (r) => lei(r.cost) },
  ];
  const colCli = [
    { cheie: "client", eticheta: "Client" },
    { cheie: "linii", eticheta: "Linii" },
    { cheie: "valoare", eticheta: "Valoare", valoare: (r) => lei(r.valoare) },
    { cheie: "cost", eticheta: "Cost", valoare: (r) => lei(r.cost) },
  ];
  if (incarca || eroare || interzis || !date) return <Stare incarca={incarca} eroare={eroare} interzis={interzis} />;

  return (
    <>
      <Panou titlu={tr("Consumul de materiale")}
        subtitlu={tr("Numărat din liniile de fișă, nu din mișcările de stoc: o piesă montată de pe mașina tehnicianului e consum la client chiar dacă mișcarea s-a făcut altundeva, iar un transfer între gestiuni nu e consum deloc.")}
        export={<Export raport="consum-materiale" titlu={tr("Consum materiale")} filtru={filtru}
          tabele={() => [
            { titlu: "Pe articol", coloane: colArt, randuri: date.peArticol },
            { titlu: "Pe client", coloane: colCli, randuri: date.peClient },
          ]} />}>
        <Tabel coloane={colArt} randuri={date.peArticol} gol="Niciun material consumat în perioada aleasă." />
      </Panou>
      <Panou titlu={tr("Pe client")}>
        <Tabel coloane={colCli} randuri={date.peClient} />
      </Panou>
    </>
  );
}

// ── 9. Garanții ───────────────────────────────────────────────────────────

function Garantii({ filtru }) {
  const { date, eroare, interzis, incarca } = useRaport(api.rapGarantii, filtru);
  const col = [
    { cheie: "client", eticheta: "Client", valoare: (r) => r.client?.denumire },
    {
      cheie: "echipament", eticheta: "Echipament",
      valoare: (r) => r.echipament?.eticheta ?? r.echipament?.tipComponenta?.denumire ?? "—",
    },
    { cheie: "serie", eticheta: "Serie", valoare: (r) => r.echipament?.serie ?? "—" },
    { cheie: "locatie", eticheta: "Locație", valoare: (r) => r.echipament?.locatie?.denumire ?? "—" },
    { cheie: "sistem", eticheta: "Sistem", valoare: (r) => r.echipament?.sistemInstalat?.denumire ?? "—" },
    { cheie: "tip", eticheta: "Tip", valoare: (r) => eticheta(TIPURI_GARANTIE, r.tip) },
    { cheie: "incepeLa", eticheta: "Începe", valoare: (r) => data(r.incepeLa) },
    { cheie: "expiraLa", eticheta: "Expiră", valoare: (r) => data(r.expiraLa) },
    {
      cheie: "zileRamase", eticheta: "Zile",
      valoare: (r) => (r.zileRamase < 0 ? `expirată de ${-r.zileRamase}` : r.zileRamase),
      clasa: (r) => (r.zileRamase < 0 ? "vechime-90" : r.zileRamase <= 30 ? "vechime-60" : ""),
    },
  ];
  if (incarca || eroare || interzis || !date) return <Stare incarca={incarca} eroare={eroare} interzis={interzis} />;

  return (
    <>
      <Panou titlu={tr("Garanții care expiră")}
        subtitlu={tr("Implicit, următoarele 90 de zile. Filtrul „Până la” mută fereastra.")}
        export={<Export raport="situatie-garantii" titlu={tr("Situația garanțiilor")} filtru={filtru}
          tabele={() => [
            { titlu: "Active", coloane: col, randuri: date.active },
            { titlu: "Expirate", coloane: col, randuri: date.expirate },
          ]} />}>
        <Tabel coloane={col} randuri={date.active} gol="Nicio garanție care expiră în fereastra aleasă." />
      </Panou>
      <Panou titlu={tr("Deja expirate")}>
        <Tabel coloane={col} randuri={date.expirate} gol="Nicio garanție expirată." />
      </Panou>
    </>
  );
}

// ── 10. Registrul de intervenții ──────────────────────────────────────────

function Registru({ filtru }) {
  const { date, eroare, interzis, incarca } = useRaport(api.rapRegistru, filtru);
  const col = [
    { cheie: "data", eticheta: "Data", valoare: (r) => data(r.data) },
    { cheie: "numar", eticheta: "Fișă" },
    { cheie: "vizitaNumar", eticheta: "Vizită", valoare: (r) => r.vizita?.numar },
    { cheie: "client", eticheta: "Client", valoare: (r) => r.vizita?.client?.denumire },
    { cheie: "locatie", eticheta: "Locație", valoare: (r) => r.vizita?.locatie?.denumire },
    { cheie: "tehnician", eticheta: "Tehnician", valoare: (r) => r.vizita?.tehnicianPrincipal?.numeComplet ?? "—" },
    { cheie: "scopInterventie", eticheta: "Scop" },
    { cheie: "remedieriEfectuate", eticheta: "Ce s-a remediat" },
    { cheie: "durataMinute", eticheta: "Durată", valoare: (r) => durata(r.durataMinute) },
    { cheie: "status", eticheta: "Stare", valoare: (r) => eticheta(STARI_VIZITA, r.status) },
    { cheie: "semnatar", eticheta: "Semnat de", valoare: (r) => r.numeSemnatarClient ?? "—" },
  ];
  if (incarca || eroare || interzis || !date) return <Stare incarca={incarca} eroare={eroare} interzis={interzis} />;

  return (
    <Panou titlu={tr("Registrul de intervenții")}
      subtitlu={tr("Jurnalul cronologic al tuturor vizitelor. Cuprinde și fișele stornate: un registru care ascunde corecțiile nu mai e un registru.")}
      export={<Export raport="registru-interventii" titlu={tr("Registru intervenții")} filtru={filtru}
        tabele={() => [{ titlu: "Registru", coloane: col, randuri: date }]} />}>
      <Tabel coloane={col} randuri={date} gol="Nicio intervenție în perioada aleasă." />
    </Panou>
  );
}
